<script lang="ts">
  import { onMount } from "svelte";
  import {
    entities,
    getWithJwt,
    postWithJwt,
    serverlessRoutes,
    translate,
    errorTracker,
    parseDateWithTime,
    fetchUnreadAiActions,
  } from "lib";
  import { InfiniteScroll, Svg } from "ui";
  import { slide } from "svelte/transition";
  import { push } from "svelte-spa-router";
  import TopMenu from "../../components/TopMenu.svelte";
  import Spinner from "../../components/UI/Spinner.svelte";
  import { unreadAiActions } from "../../stores/notificationStore";

  // Server payload — `id` arrives namespaced as "ai_action:N" or
  // "profile_history:N". Use the full string as the React/Svelte key, but pass
  // only the numeric tail to /:id detail and /:id/rate endpoints.
  type AiAction = {
    id: string;
    kind: "ai_action" | "profile_history" | string;
    action_type: string;
    client_id: number;
    trainer_id: number;
    summary: string | null;
    input_payload: any;
    output_payload: any;
    entity_type: string | null;
    entity_id: number | null;
    status: string;
    rating: number | null;
    rated_by: number | null;
    rated_at: string | null;
    createdAt: string;
  };

  // Category chips per spec — each maps to a (possibly multi-value) `types`
  // filter and an optional `includeProfileUpdates` toggle.
  type ChipKey = "all" | "comments" | "workouts" | "exercises" | "profile";
  type Chip = {
    key: ChipKey;
    label: string;
    types?: string[];
    includeProfileUpdates?: boolean;
  };

  const CHIPS: Chip[] = [
    { key: "all", label: "Sve", includeProfileUpdates: true },
    {
      key: "comments",
      label: "Komentari",
      types: ["comment_reply", "comment_no_reply"],
    },
    {
      key: "workouts",
      label: "Treninzi",
      types: ["workout_assigned", "workout_generated"],
    },
    {
      key: "exercises",
      label: "Zamene vežbi",
      types: ["exercise_replaced"],
    },
    {
      key: "profile",
      label: "Profil",
      types: ["profile_updated"],
      includeProfileUpdates: true,
    },
  ];

  // Solid backgrounds for the small type badge — chosen so they stay readable
  // in both light and dark mode.
  const TYPE_BADGE_CLASSES: Record<string, string> = {
    comment_reply:
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
    comment_no_reply:
      "bg-slate-100 text-slate-700 dark:bg-zinc-700 dark:text-slate-200",
    workout_assigned:
      "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
    workout_generated:
      "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200",
    exercise_replaced:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    profile_updated:
      "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200",
  };

  const TYPE_LABELS: Record<string, string> = {
    comment_reply: "Odgovor na komentar",
    comment_no_reply: "Komentar (bez odgovora)",
    workout_assigned: "Trening dodeljen",
    workout_generated: "Trening generisan",
    exercise_replaced: "Vežba zamenjena",
    profile_updated: "Profil ažuriran",
  };

  const PAGE_SIZE = 50;

  let items: AiAction[] = [];
  let count = 0;
  let isLoading = true;
  let isFetchingMore = false;
  let isRating: Record<string, boolean> = {};
  let activeChip: ChipKey = "all";
  let onlyUnrated = false;
  let expandedId: string | null = null;

  const buildUrl = (skip: number) => {
    const params = new URLSearchParams();
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String(skip));

    // Resolve the chip from activeChip directly here — going through the
    // `currentChip` reactive would read a stale value, because `$:` blocks
    // run on the next microtask but onChipClick fires the fetch
    // synchronously after assigning `activeChip`. Reading activeChip
    // straight from the latest assignment avoids that race.
    const chip = CHIPS.find((c) => c.key === activeChip) ?? CHIPS[0];

    if (chip.types && chip.types.length) {
      params.set("types", chip.types.join(","));
    }
    if (chip.includeProfileUpdates) {
      params.set("includeProfileUpdates", "1");
    }
    if (onlyUnrated) {
      params.set("onlyUnrated", "1");
    }
    return `${serverlessRoutes.AI_ACTION}?${params.toString()}`;
  };

  const fetchPage = async (skip: number) => {
    try {
      const res = await getWithJwt(buildUrl(skip));
      const payload = res?.data ?? {};
      const fetched: AiAction[] = Array.isArray(payload.items)
        ? payload.items
        : [];
      const fetchedCount: number =
        typeof payload.count === "number" ? payload.count : fetched.length;
      if (skip === 0) {
        items = fetched;
      } else {
        items = [...items, ...fetched];
      }
      count = fetchedCount;
    } catch (err) {
      errorTracker.captureError(err as Error | string, "error", {
        context: "ai_actions",
        action: "fetch_page",
        skip,
        chip: activeChip,
        onlyUnrated,
      });
    }
  };

  const onLoadMore = async () => {
    if (isFetchingMore) return;
    if (items.length >= effectiveTotal) return;
    isFetchingMore = true;
    await fetchPage(items.length);
    isFetchingMore = false;
  };

  const reload = async () => {
    isLoading = true;
    items = [];
    count = 0;
    expandedId = null;
    await fetchPage(0);
    isLoading = false;
  };

  const onChipClick = async (key: ChipKey) => {
    if (activeChip === key) return;
    activeChip = key;
    await reload();
  };

  const onUnratedToggle = async () => {
    onlyUnrated = !onlyUnrated;
    await reload();
  };

  const toggle = (id: string) => {
    expandedId = expandedId === id ? null : id;
  };

  // Strip the "ai_action:" / "profile_history:" prefix to get the numeric id.
  const numericId = (id: string): number | null => {
    if (typeof id !== "string") return null;
    const tail = id.includes(":") ? id.split(":")[1] : id;
    const n = Number(tail);
    return Number.isFinite(n) ? n : null;
  };

  const isProfileHistory = (action: AiAction): boolean =>
    action.kind === "profile_history";

  const rate = async (action: AiAction, rating: 1 | -1 | null) => {
    // Profile-history rows have no underlying ai_action — skip the call.
    if (isProfileHistory(action)) return;
    const n = numericId(action.id);
    if (n == null) return;
    if (isRating[action.id]) return;

    // Optimistic — clicking the same thumb again clears the rating.
    const next = action.rating === rating ? null : rating;
    isRating = { ...isRating, [action.id]: true };
    const previous = action.rating;
    items = items.map((a) =>
      a.id === action.id ? { ...a, rating: next } : a
    );

    // Optimistically update the "unread" badge: going from null → rating
    // makes the row read (-1 from the badge); going from rated → null
    // makes it unread again (+1). Same-thumb double-click flows through
    // both branches above (rated → null), so the math stays right.
    const badgeDelta =
      previous === null && next !== null
        ? -1
        : previous !== null && next === null
        ? 1
        : 0;
    if (badgeDelta !== 0) {
      unreadAiActions.update((n) => Math.max(0, n + badgeDelta));
    }

    try {
      const res = await postWithJwt(
        `${serverlessRoutes.AI_ACTION}/${n}/rate`,
        { rating: next }
      );
      const updated = res?.data;
      if (updated && typeof updated === "object") {
        items = items.map((a) =>
          a.id === action.id
            ? {
                ...a,
                rating: updated.rating ?? next,
                rated_by: updated.rated_by ?? a.rated_by,
                rated_at: updated.rated_at ?? a.rated_at,
              }
            : a
        );
      }
    } catch (err) {
      // Revert on failure — both the row and the badge.
      items = items.map((a) =>
        a.id === action.id ? { ...a, rating: previous } : a
      );
      if (badgeDelta !== 0) {
        unreadAiActions.update((n) => Math.max(0, n - badgeDelta));
      }
      errorTracker.captureError(err as Error | string, "error", {
        context: "ai_actions",
        action: "rate",
        id: action.id,
        rating: next,
      });
    } finally {
      isRating = { ...isRating, [action.id]: false };
    }
  };

  // Drill-in routing per entity_type. We don't always know the parent context
  // (e.g. workout's program), so fall back to the client's matching tab.
  const drillIn = async (action: AiAction) => {
    const clientId = action.client_id;
    const out = action.output_payload || {};
    switch (action.entity_type) {
      case "program":
        if (action.entity_id) {
          await push(
            `/profile/${clientId}/training/programs/${action.entity_id}`
          );
        } else {
          await push(`/profile/${clientId}?view=training&type=active`);
        }
        return;
      case "workout": {
        const programId = out.program_id ?? out.programId;
        if (programId) {
          await push(
            `/profile/${clientId}/training/programs/${programId}#workout-${action.entity_id}`
          );
        } else {
          await push(`/profile/${clientId}?view=training&type=active`);
        }
        return;
      }
      case "exercise": {
        const programId = out.program_id ?? out.programId;
        const workoutId = out.workout_id ?? out.workoutId;
        if (programId && workoutId) {
          await push(
            `/profile/${clientId}/training/programs/${programId}#exercise-${workoutId}-${action.entity_id}`
          );
        } else {
          await push(`/profile/${clientId}?view=training&type=active`);
        }
        return;
      }
      case "meal": {
        const mealPlanId = out.meal_plan_id ?? out.mealPlanId;
        if (mealPlanId) {
          await push(
            `/profile/${clientId}/diet/meal-plans/${mealPlanId}#meal-${action.entity_id}`
          );
        } else {
          await push(`/profile/${clientId}?view=diet&type=active`);
        }
        return;
      }
      case "comment":
        // Anticipated but not used yet — fall back to the client profile.
        await push(`/profile/${clientId}`);
        return;
      case "profile_field": {
        const fieldPath = out.field_path ?? "";
        const suffix = fieldPath
          ? `?view=ai-profile&field=${encodeURIComponent(fieldPath)}`
          : "?view=ai-profile";
        await push(`/profile/${clientId}${suffix}`);
        return;
      }
      default:
        if (clientId) await push(`/profile/${clientId}`);
    }
  };

  const translateActionType = (actionType: string): string =>
    TYPE_LABELS[actionType] || actionType;

  const badgeClass = (actionType: string): string =>
    TYPE_BADGE_CLASSES[actionType] ||
    "bg-slate-100 text-slate-700 dark:bg-zinc-700 dark:text-slate-200";

  // Use the same locale-aware date+time formatter that Chat and other
  // surfaces use, so the AI Log timestamps read consistently across the app
  // (e.g. "ned, 10. maj 2026, 19:16" in Serbian).
  const formatDateTime = (iso: string): string => {
    if (!iso) return "";
    return parseDateWithTime(iso);
  };

  const stringifyPayload = (payload: unknown): string => {
    if (payload == null) return "";
    if (typeof payload === "string") return payload;
    try {
      return JSON.stringify(payload, null, 2);
    } catch {
      return String(payload);
    }
  };

  // `count` only reflects ai_action rows. When merged-in profile_history rows
  // are present, fall back to `items.length` for the "+1, +2…" estimate.
  $: hasMergedProfile =
    !!(CHIPS.find((c) => c.key === activeChip)?.includeProfileUpdates) &&
    items.some((i) => i.kind === "profile_history");
  $: effectiveTotal = hasMergedProfile
    ? Math.max(count, items.length)
    : count;

  onMount(async () => {
    await fetchPage(0);
    isLoading = false;
    // Re-sync the blue badge with reality whenever the screen opens — covers
    // the cases where a cron added rows since boot, or another trainer (on
    // a shared account) rated some entries.
    fetchUnreadAiActions();
  });
</script>

<TopMenu entity={entities.NOTIFICATIONS} />
<div class="bg-white dark:bg-zinc-800 text-slate-900 dark:text-slate-100">
  <!-- Category chips -->
  <div class="px-3 pt-3 flex flex-wrap items-center gap-2">
    {#each CHIPS as chip}
      <button
        type="button"
        on:click={() => onChipClick(chip.key)}
        class="text-xs px-3 py-1 rounded-full border whitespace-nowrap transition-colors
          {activeChip === chip.key
          ? 'bg-primary-500 text-white border-primary-500'
          : 'bg-white text-slate-700 border-slate-300 dark:bg-zinc-700 dark:text-slate-200 dark:border-zinc-600'}"
      >
        {chip.label}
      </button>
    {/each}

    <button
      type="button"
      on:click={onUnratedToggle}
      class="text-xs px-3 py-1 rounded-full border whitespace-nowrap transition-colors ml-auto
        {onlyUnrated
        ? 'bg-amber-500 text-white border-amber-500'
        : 'bg-white text-slate-700 border-slate-300 dark:bg-zinc-700 dark:text-slate-200 dark:border-zinc-600'}"
      title="Prikaži samo neocenjene"
    >
      Samo neocenjeni
    </button>
  </div>

  {#if isLoading}
    <Spinner />
  {:else if items.length === 0}
    <h3 class="text-center" style="margin-top: 64px;">
      {translate("AI_ACTIONS_EMPTY")}
    </h3>
  {:else}
    <ul class="flex flex-col px-3 pb-4">
      {#each items as action (action.id)}
        {@const isOpen = expandedId === action.id}
        {@const profileHistory = isProfileHistory(action)}
        {@const rateBusy = !!isRating[action.id]}
        <li
          class="border-b border-slate-200 dark:border-zinc-700 last:border-b-0"
        >
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div
            role="button"
            tabindex="0"
            class="py-3 px-1 flex items-start gap-3 cursor-pointer"
            on:click={() => toggle(action.id)}
            on:keypress={() => toggle(action.id)}
          >
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1 flex-wrap">
                <span
                  class="text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap {badgeClass(
                    action.action_type
                  )}"
                >
                  {translateActionType(action.action_type)}
                </span>
                <span
                  class="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap"
                >
                  {formatDateTime(action.createdAt)}
                </span>
                {#if action.rating === 1}
                  <span class="text-xs">👍</span>
                {:else if action.rating === -1}
                  <span class="text-xs">👎</span>
                {/if}
              </div>
              {#if action.summary}
                <p
                  class="text-sm text-slate-800 dark:text-slate-100 leading-snug break-words"
                >
                  {action.summary}
                </p>
              {/if}
            </div>
            <div
              class="chevron shrink-0 text-slate-400 dark:text-slate-500 mt-1"
              class:isOpen
            >
              <Svg name="down-caret" size={14} />
            </div>
          </div>

          {#if isOpen}
            <div
              class="px-1 pb-3 pt-1 flex flex-col gap-2 text-xs text-slate-700 dark:text-slate-300"
              transition:slide
            >
              <div class="flex flex-wrap gap-x-4 gap-y-1">
                <span>
                  <span class="text-slate-500 dark:text-slate-400">Klijent:</span>
                  {action.client_id}
                </span>
                {#if action.entity_type}
                  <span>
                    <span class="text-slate-500 dark:text-slate-400">Entitet:</span>
                    {action.entity_type}{action.entity_id
                      ? ` #${action.entity_id}`
                      : ""}
                  </span>
                {/if}
                <span>
                  <span class="text-slate-500 dark:text-slate-400">Kada:</span>
                  {formatDateTime(action.createdAt)}
                </span>
              </div>

              <!-- Action row: drill-in + rating (rating hidden for profile_history) -->
              <div class="flex items-center gap-2 mt-1 flex-wrap">
                {#if action.entity_type || action.client_id}
                  <button
                    type="button"
                    class="text-xs px-2 py-1 rounded border border-slate-300 dark:border-zinc-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-zinc-700"
                    on:click|stopPropagation={() => drillIn(action)}
                  >
                    Otvori
                  </button>
                {/if}

                {#if !profileHistory}
                  <button
                    type="button"
                    disabled={rateBusy}
                    aria-pressed={action.rating === 1}
                    class="text-sm px-2 py-1 rounded border transition-colors
                      {action.rating === 1
                      ? 'bg-emerald-500 text-white border-emerald-500'
                      : 'bg-white text-slate-700 border-slate-300 dark:bg-zinc-700 dark:text-slate-200 dark:border-zinc-600'}"
                    on:click|stopPropagation={() => rate(action, 1)}
                    title="Dobro"
                  >
                    👍
                  </button>
                  <button
                    type="button"
                    disabled={rateBusy}
                    aria-pressed={action.rating === -1}
                    class="text-sm px-2 py-1 rounded border transition-colors
                      {action.rating === -1
                      ? 'bg-rose-500 text-white border-rose-500'
                      : 'bg-white text-slate-700 border-slate-300 dark:bg-zinc-700 dark:text-slate-200 dark:border-zinc-600'}"
                    on:click|stopPropagation={() => rate(action, -1)}
                    title="Loše"
                  >
                    👎
                  </button>
                {/if}
              </div>

              {#if action.input_payload != null}
                <div>
                  <div class="text-slate-500 dark:text-slate-400 mb-1">
                    Input
                  </div>
                  <pre
                    class="whitespace-pre-wrap break-words text-[11px] bg-slate-50 dark:bg-zinc-900/60 p-2 rounded border border-slate-200 dark:border-zinc-700">{stringifyPayload(action.input_payload)}</pre>
                </div>
              {/if}
              {#if action.output_payload != null}
                <div>
                  <div class="text-slate-500 dark:text-slate-400 mb-1">
                    Output
                  </div>
                  <pre
                    class="whitespace-pre-wrap break-words text-[11px] bg-slate-50 dark:bg-zinc-900/60 p-2 rounded border border-slate-200 dark:border-zinc-700">{stringifyPayload(action.output_payload)}</pre>
                </div>
              {/if}
            </div>
          {/if}
        </li>
      {/each}
    </ul>

    <InfiniteScroll
      hasMore={items.length < effectiveTotal}
      on:loadMore={onLoadMore}
    />
  {/if}
</div>

<style>
  .chevron {
    transition: transform 200ms ease;
  }
  .chevron.isOpen {
    transform: rotate(180deg);
  }
</style>
